import { DateTime, Interval } from 'luxon';
import { useEffect } from 'react';
import { DAY, END_DATE, START_DATE } from '../constants/dateConstants';
import { DEFAULT_DATE_FORMAT } from '../constants/dateFormatConstants';
import {
  getAvailability,
  getIndexes,
  getIsBridging,
  getIsSelected,
} from '../utils/calendarDataUtils';
import {
  setAllPossibleDates,
  setCalendarData,
  setDates,
  setFocusedField,
} from '../ducks/dateDuck';
import { setEndTime, setStartTime } from '../ducks/timeDuck';

export const useAllPossibleDates = ({
  parentMaxDaysAdvanceFromToday,
  parentMinStartDateString,
  dateDispatch,
}) => {
  useEffect(() => {
    let earliestDateLuxon = DateTime.now().startOf(DAY);
    if (parentMinStartDateString) {
      const parentMinStartDateLuxon = DateTime.fromFormat(
        parentMinStartDateString,
        DEFAULT_DATE_FORMAT
      );
      if (parentMinStartDateLuxon.isValid) {
        earliestDateLuxon = parentMinStartDateLuxon;
      }
    }
    const latestDateLuxon = earliestDateLuxon.plus({
      days: parentMaxDaysAdvanceFromToday,
    });
    const intervalLuxon = Interval.fromDateTimes(
      earliestDateLuxon,
      latestDateLuxon
    ).splitBy({ days: 1 });
    const allPossibleDatesAsStrings = intervalLuxon.reduce(
      (accumulator, { start }, index) => {
        accumulator[start.toFormat(DEFAULT_DATE_FORMAT)] = index;
        return accumulator;
      },
      {}
    );
    const allPossibleDatesByIndex = intervalLuxon.reduce(
      (accumulator, { start }, index) => {
        accumulator[index] = start.toFormat(DEFAULT_DATE_FORMAT);
        return accumulator;
      },
      {}
    );
    dateDispatch(
      setAllPossibleDates({
        allPossibleDatesAsStrings,
        allPossibleDatesByIndex,
      })
    );
  }, [parentMaxDaysAdvanceFromToday, parentMinStartDateString, dateDispatch]);
};

export const useCalendarData = ({
  dateState: {
    startDate,
    endDate,
    focusedField,
    allPossibleDatesByIndex,
    allPossibleDatesAsStrings,
  },
  parentMaxDaysAdvanceFromToday: maxDaysAdvanceFromToday,
  minNights,
  maxNights,
  minNightsPerDate,
  pricePerDate,
  dateDispatch,
  datesAvailability,
  isInline,
}) => {
  useEffect(() => {
    if (!allPossibleDatesByIndex || !allPossibleDatesAsStrings) {
      return;
    }
    const { latestAvailableIndex, earliestAvailableIndex } = getIndexes({
      maxDaysAdvanceFromToday,
      minNights,
      focusedField,
      startDate,
      minNightsPerDate,
      allPossibleDatesAsStrings,
      maxNights,
    });
    const availableDatesInRange = Object.keys(allPossibleDatesAsStrings).filter(
      (dateString) => {
        const dateIndex = allPossibleDatesAsStrings[dateString];
        return (
          dateIndex >= earliestAvailableIndex
          && dateIndex <= latestAvailableIndex
        );
      }
    );

    let filteredAvailableDates = availableDatesInRange;
    if (datesAvailability) {
      let shouldSkip = false;
      filteredAvailableDates = availableDatesInRange.filter(
        (dateString, dateStringIndex) => {
          if (shouldSkip) {
            return false;
          }
          if (!datesAvailability[dateString]?.isAvailableAsBridgingDate) {
            if (focusedField === END_DATE) {
              shouldSkip = true;
            }
            return false;
          }

          const isAvailable = getAvailability({
            allPossibleDatesByIndex,
            datesAvailability,
            dateString,
            dateStringIndex,
            focusedField,
            minNights,
            minNightsPerDate,
            isInline,
          });

          return isAvailable;
        }
      );
    }

    const startDateIndex = allPossibleDatesAsStrings[startDate];
    const endDateIndex = allPossibleDatesAsStrings[endDate];

    const joinedData = Object.keys(allPossibleDatesAsStrings).reduce(
      (joinedCalendarInfo, dayString) => {
        const dateIndex = allPossibleDatesAsStrings[dayString];
        const isSelected = getIsSelected(
          startDateIndex,
          dateIndex,
          endDateIndex
        );
        const isBridging = !isSelected && getIsBridging(startDateIndex, dateIndex, endDateIndex);
        return {
          ...joinedCalendarInfo,
          [dayString]: {
            dayIndex: allPossibleDatesAsStrings[dayString],
            isEnabled:
              availableDatesInRange
              && availableDatesInRange.includes(dayString),
            isAvailable:
              filteredAvailableDates
              && filteredAvailableDates.includes(dayString),
            minNights: minNightsPerDate[dayString] || minNights,
            isSelected,
            isBridging,
            price: pricePerDate && pricePerDate[dayString],
          },
        };
      },
      {}
    );
    dateDispatch(setCalendarData(joinedData));
  }, [
    startDate,
    endDate,
    focusedField,
    datesAvailability,
    allPossibleDatesAsStrings,
    allPossibleDatesByIndex,
    maxDaysAdvanceFromToday,
    minNights,
    maxNights,
    minNightsPerDate,
    pricePerDate,
    dateDispatch,
  ]);
};

export const useUpdateParentWhenStateChanges = ({
  startDate,
  endDate,
  startTime,
  endTime,
  parentValue,
  hasTimeDropdown,
  onChange,
}) => {
  useEffect(() => {
    const startDateString = startDate;
    const endDateString = endDate;

    const hasDateValueChanged = startDateString !== parentValue.startDate
      || endDateString !== parentValue.endDate;

    const hasTimeValueChanged = hasTimeDropdown
      ? startTime !== parentValue.startTime || endTime !== parentValue.endTime
      : false;

    if (hasDateValueChanged || hasTimeValueChanged) {
      onChange({
        startDate: startDateString,
        endDate: endDateString,
        ...hasTimeDropdown && {
          startTime,
          endTime,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only internal values
  }, [startDate, endDate, startTime, endTime]);
};

export const useUpdateStateWhenParentDateValueChanges = ({
  parentValue,
  startDate,
  endDate,
  dateDispatch,
}) => {
  useEffect(() => {
    if (
      !(parentValue.startDate === startDate && parentValue.endDate === endDate)
    ) {
      dateDispatch(
        setDates({
          startDate: parentValue.startDate,
          endDate: parentValue.endDate,
        })
      );
      if (parentValue.startDate && !parentValue.endDate) {
        dateDispatch(setFocusedField(START_DATE));
      } else {
        dateDispatch(setFocusedField(END_DATE));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only external values
  }, [parentValue.startDate, parentValue.endDate]);
};

export const useParentTimeValue = ({
  parentValue,
  timeDispatch,
  startTime,
  endTime,
}) => {
  useEffect(() => {
    if (parentValue.startTime !== startTime) {
      timeDispatch(setStartTime(parentValue.startTime));
    }

    if (parentValue.endTime !== endTime) {
      timeDispatch(setEndTime(parentValue.endTime));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only external values
  }, [parentValue.startTime, parentValue.endTime]);
};
